import {matchPath, useLocation} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {
  businessCaseRoutes,
  customerRoutes,
  employeeRoutes,
  interestRoutes,
  recordAuditRoutes,
  rentalOrderRoutes,
  sourcingRoutes,
  tiresInventoryRoutes,
  vehiclesRoutes,
  warehouseRoutes,
  workshopRoutes,
} from '@dms/routes';
import {useNavigationBreadCrumb} from '@dms/shared';

import {useIsMobileNavigation} from './useIsMobileNavigation';

const HIDDEN_NAVIGATION_PATH_PREFIXES = [
  vehiclesRoutes.home,
  businessCaseRoutes.home,
  interestRoutes.overview,
  workshopRoutes.interest,
];

const HIDDEN_NAVIGATION_ROUTES = [
  recordAuditRoutes.detail,
  rentalOrderRoutes.rowControlDetail,
  tiresInventoryRoutes.tireOrderDetail,
  workshopRoutes.serviceCaseDetail,
  workshopRoutes.serviceCampaignDetail,
  warehouseRoutes.articleDetailRowControl,
  warehouseRoutes.deliveryNoteDetailRowControl,
  warehouseRoutes.directSalesDetailRowControl,
  warehouseRoutes.fastTrackIssuesDetailRowControl,
  warehouseRoutes.receiveNoteDetailRowControl,
  warehouseRoutes.receiveNoteCorrectionDetailRowControl,
  warehouseRoutes.serviceOrderIssueDetailRowControl,
  warehouseRoutes.serviceOrderReturnsDetailRowControl,
  warehouseRoutes.transferOrderDetailRowControl,
  warehouseRoutes.updateJobDetailRowControl,
];

const CREATE_PAGE_ROUTES = [
  customerRoutes.create,
  employeeRoutes.createEmployee,
  workshopRoutes.createServiceCase,
  workshopRoutes.draftServiceCase,
  tiresInventoryRoutes.createTireOrder,
  businessCaseRoutes.concept,
  recordAuditRoutes.create,
];

export function useIsPageNavigationVisible() {
  const location = useLocation();
  const [breadcrumbProps, isBreadcrumbVisible] = useNavigationBreadCrumb();
  const isMobileNavigation = useIsMobileNavigation();
  const rowControlDetail = breadcrumbProps?.rowControlDetail;

  const isSourcingPage = location.pathname.includes(sourcingRoutes.home);

  const isCustomerDetailPage =
    isNotNil(matchPath({path: customerRoutes.rowControlDetail}, location.pathname)) &&
    [customerRoutes.gdprConsentsOverview, customerRoutes.create].every((path) =>
      isNil(matchPath({path}, location.pathname))
    );

  const isEmployeeDetailPage =
    isNotNil(matchPath({path: employeeRoutes.employeeDetailRowControl}, location.pathname)) &&
    isNil(matchPath({path: employeeRoutes.userAbsenceOverview}, location.pathname));

  const shouldHideNavigation =
    isMobileNavigation &&
    (HIDDEN_NAVIGATION_PATH_PREFIXES.some((path) =>
      isNotNil(matchPath({path, end: false}, location.pathname))
    ) ||
      isCustomerDetailPage ||
      isEmployeeDetailPage ||
      HIDDEN_NAVIGATION_ROUTES.some((path) => isNotNil(matchPath({path}, location.pathname))));

  const isCreatePage =
    isMobileNavigation &&
    CREATE_PAGE_ROUTES.some((path) => isNotNil(matchPath({path}, location.pathname)));

  if (shouldHideNavigation || isCreatePage) {
    return false;
  }

  return Boolean(isBreadcrumbVisible || rowControlDetail || isSourcingPage);
}
